import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { graphql, useFragment } from "react-relay";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faChevronLeft, faChevronRight, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { format } from "date-fns";
import { noop } from "lodash";
import type { Moment } from "moment";
// @ts-ignore-line
import { doesNodeContainClick } from "semantic-ui-react/dist/commonjs/lib";

import { AgendaDatePicker } from "~/common/DatePicker";
import { inCalendarMode, isMobile } from "~/lib/utils";
import { Button } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import { useTourContext } from "~/welcomeChecklist";

import MeetingsPanelMenu from "./MeetingsPanelMenu";
import type { ViewMode } from "./utils";

import type { MeetingsPanelHeader_viewer$key as ViewerKey } from "~/__generated__/MeetingsPanelHeader_viewer.graphql";

import styles from "./MeetingsPanel.module.css";

const viewerFragment = graphql`
	fragment MeetingsPanelHeader_viewer on Viewer {
		uxSimplificationEnabled
		...MeetingsPanelMenu_viewer
		...DatePickerAgenda_viewer
	}
`;

interface MeetingsPanelHeaderProps {
	viewer: ViewerKey;
	visible: boolean;

	current: Moment;
	onDateChange: (date: Moment) => void;
	onDateToday: () => void;

	dropdownDate: Date;
	focused: boolean;
	onDateFocus: (focused: boolean | null) => void;

	viewMode: ViewMode;
	changeViewMode: (viewMode: string) => void;

	closeGoogleCalendarPanel: () => void;
	openEventCreationModal: () => void;
}

export default function MeetingsPanelHeader({
	viewer: viewerKey,
	visible,
	current,
	onDateChange,
	onDateToday,
	dropdownDate,
	focused: focusedProp,
	onDateFocus,
	viewMode,
	changeViewMode,
	closeGoogleCalendarPanel,
	openEventCreationModal,
}: MeetingsPanelHeaderProps) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const [focused, setFocused] = useState(focusedProp);
	const dropdownRef = useRef(null);
	const calendarRef = useRef(null);
	const shortDateString = useMemo(() => format(dropdownDate, "EEE, MMM d"), [dropdownDate]);

	useEffect(() => {
		onDateFocus(focused);
	}, [onDateFocus, focused]);

	const onChildFocusChange = useCallback(
		(value: boolean | null) => {
			setFocused(value === null ? !focused : value);
		},
		[focused, setFocused],
	);

	const toggle = useCallback(() => setFocused(!focused), [focused, setFocused]);
	const hide = useCallback(
		(e: MouseEvent) => {
			if (!calendarRef || !doesNodeContainClick(calendarRef.current, e)) {
				setFocused(false);
			}
		},
		[setFocused],
	);

	const navigate = useCallback(
		(direction: "next" | "prev") => () => {
			switch (direction) {
				case "next":
					return onDateChange(current.clone().add(1, "day"));
				case "prev":
					return onDateChange(current.clone().subtract(1, "day"));
			}
		},
		[onDateChange, current],
	);

	const { activeTour } = useTourContext();
	const isTourActive = Boolean(activeTour);

	return (
		<>
			<header className={styles.panelHeader}>
				<h1 className="agenda-heading">Meetings</h1>

				<div className={styles.rightSideButtons}>
					{(!viewer.uxSimplificationEnabled || !inCalendarMode) && (
						<IconButton
							title="Create"
							onClick={openEventCreationModal}
							className={cx(styles.panelButton, styles.createButton)}
							icon={faPlus}
							data-test-id="add-event-button"
							disabled={isTourActive}
						/>
					)}
					{!viewer.uxSimplificationEnabled && inCalendarMode ? (
						<IconButton
							title="Close panel"
							onClick={closeGoogleCalendarPanel}
							className={cx(styles.panelButton, styles.cornerButton)}
							icon={faTimes}
						/>
					) : (
						<MeetingsPanelMenu
							viewer={viewer}
							onChange={noop}
							panelOpen={visible}
							viewMode={viewMode}
							changeViewMode={changeViewMode}
							disabled={isTourActive}
						/>
					)}
				</div>
			</header>
			<div className={styles.calendarNavButtons}>
				<OutsideClickHandler onOutsideClick={hide}>
					<Button variant="secondary" size="slim" data-test-id="datepicker-toggle" ref={dropdownRef} onClick={toggle}>
						{shortDateString}&ensp;
						<FontAwesomeIcon icon={focused ? faCaretUp : faCaretDown} />
					</Button>
					{focused && (
						<div className={styles.datePickerContainer}>
							<AgendaDatePicker
								viewer={viewer}
								date={current}
								selectorElem="date-picker-entry-point"
								onDateChange={onDateChange}
								onFocusChange={onChildFocusChange}
								calendarRef={calendarRef}
								// Values below may look weird but there's some logic behind them.
								// For mobile the Big Calendar component's size is 20px of padding + day's width
								// We have 7 days in a row, so if we do (window.innerWidth-20)/7 we get the width for each day.
								// For desktop 37.1 here may sound weird but it's actually somewhat intentional.
								// Is the only possible way to round the Big Calendar's day picker to the width that we want.
								// Based on our current's navs width. In case that changes this would need to change.
								daySize={isMobile ? (window.innerWidth - 20) / 7 : 37.1}
							/>
						</div>
					)}
				</OutsideClickHandler>

				<div className={styles.rightNav}>
					{viewMode === "day" && (
						<>
							<IconButton className={styles.navArrow} icon={faChevronLeft} onClick={navigate("prev")} />
							<IconButton className={styles.navArrow} icon={faChevronRight} onClick={navigate("next")} />
						</>
					)}
					<Button variant="secondary" size="slim" onClick={onDateToday}>
						Today
					</Button>
				</div>
			</div>
			<div id="date-picker-entry-point" style={{ position: "relative" }} />
		</>
	);
}
